<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import {
  useRoute,
  useRouter,
  type RouteLocationNormalizedLoaded,
  type Router,
} from "vue-router";
import type { TenantSummary } from "../api/tenants.js";
import {
  createNotificationContact,
  createNotificationGroup,
  createNotificationTemplate,
  deleteNotificationContact,
  deleteNotificationGroup,
  deleteNotificationTemplate,
  listNotificationContacts,
  listNotificationGroups,
  listNotificationPolicies,
  listNotificationTemplates,
  updateNotificationContact,
  updateNotificationGroup,
  updateNotificationPolicy,
  updateNotificationTemplate,
  type NotificationContact,
  type NotificationContactStatus,
  type NotificationEventFamily,
  type NotificationEventPolicy,
  type NotificationGroup,
  type NotificationTemplate,
} from "../api/notifications.js";
import { ApiRequestError } from "../api/http.js";
import AppShell from "../components/AppShell.vue";
import { confirmDialog } from "../dialogs/app-dialog.js";
import { formatDateTime } from "../formatting/date-time.js";
import { useTenantStore } from "../stores/tenants.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

type NotificationTab = "contacts" | "groups" | "templates" | "policies";
type EditableContactStatus = "pending" | "active" | "disabled";

interface PolicyForm {
  readonly eventFamily: NotificationEventFamily;
  enabled: boolean;
  thresholdMinutes: number | string;
  lastThresholdOverrideMinutes: number | string | null;
  thresholdOverrideEnabled: boolean;
  sendOpened: boolean;
  sendRecovery: boolean;
  openedTemplateId: string;
  resolvedTemplateId: string;
  groupIds: string[];
}

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const route: RouteLocationNormalizedLoaded = useRoute();
const router: Router = useRouter();
const activeTab: Ref<NotificationTab> = ref("contacts");
const loading: Ref<boolean> = ref(false);
const saving: Ref<boolean> = ref(false);
const message: Ref<string | null> = ref(null);
const errorMessage: Ref<string | null> = ref(null);
const contacts: Ref<NotificationContact[]> = ref([]);
const groups: Ref<NotificationGroup[]> = ref([]);
const templates: Ref<NotificationTemplate[]> = ref([]);
const policies: Ref<NotificationEventPolicy[]> = ref([]);
const selectedContactId: Ref<string | null> = ref(null);
const selectedGroupId: Ref<string | null> = ref(null);
const selectedTemplateId: Ref<string | null> = ref(null);
const contactEmailFilter: Ref<string> = ref("");
const contactNameFilter: Ref<string> = ref("");
const contactStatusFilter: Ref<string> = ref("");
const groupContactEmailFilter: Ref<string> = ref("");
const groupContactNameFilter: Ref<string> = ref("");
const groupContactStatusFilter: Ref<string> = ref("");
const contactEmail: Ref<string> = ref("");
const contactDisplayName: Ref<string> = ref("");
const contactSalutation: Ref<string> = ref("");
const contactStatus: Ref<EditableContactStatus> = ref("pending");
const groupName: Ref<string> = ref("");
const groupDescription: Ref<string> = ref("");
const groupEnabled: Ref<boolean> = ref(true);
const groupContactIds: Ref<string[]> = ref([]);
const templateName: Ref<string> = ref("");
const templateDescription: Ref<string> = ref("");
const templateEnabled: Ref<boolean> = ref(true);
const templateSubject: Ref<string> = ref("[{{tenant_name}}] {{probe_name}}: {{current_state}}");
const templateText: Ref<string> = ref("{{recipient_salutation}}\n\n{{product_name}} detected {{current_state}} for {{probe_name}} in {{tenant_name}}.\n\nStarted: {{event_started_at}}\nThreshold: {{threshold_duration}}\nDetails: {{dashboard_url}}");
const templateHtml: Ref<string> = ref("");
const policyForms: Ref<PolicyForm[]> = ref([
  {
    eventFamily: "internet_interruption",
    enabled: false,
    thresholdMinutes: 5,
    lastThresholdOverrideMinutes: null,
    thresholdOverrideEnabled: false,
    sendOpened: true,
    sendRecovery: true,
    openedTemplateId: "",
    resolvedTemplateId: "",
    groupIds: [],
  },
  {
    eventFamily: "probe_unavailable",
    enabled: false,
    thresholdMinutes: 5,
    lastThresholdOverrideMinutes: null,
    thresholdOverrideEnabled: false,
    sendOpened: true,
    sendRecovery: true,
    openedTemplateId: "",
    resolvedTemplateId: "",
    groupIds: [],
  },
]);

const tenant: ComputedRef<TenantSummary | null> = computed(
  (): TenantSummary | null => tenantStore.selectedTenant,
);
const canManage: ComputedRef<boolean> = computed(
  (): boolean => tenant.value?.access.capabilities.includes("tenant.notifications.manage") ?? false,
);
const activeContactCount: ComputedRef<number> = computed(
  (): number => contacts.value.filter((contact: NotificationContact): boolean =>
    contact.status === "active" || contact.status === "pending",
  ).length,
);
const selectedContact: ComputedRef<NotificationContact | null> = computed(
  (): NotificationContact | null =>
    contacts.value.find((contact: NotificationContact): boolean => contact.id === selectedContactId.value) ?? null,
);
const selectedGroup: ComputedRef<NotificationGroup | null> = computed(
  (): NotificationGroup | null =>
    groups.value.find((group: NotificationGroup): boolean => group.id === selectedGroupId.value) ?? null,
);
const selectedTemplate: ComputedRef<NotificationTemplate | null> = computed(
  (): NotificationTemplate | null =>
    templates.value.find((template: NotificationTemplate): boolean => template.id === selectedTemplateId.value) ?? null,
);
const contactStatusOptions: ComputedRef<readonly EditableContactStatus[]> = computed(
  (): readonly EditableContactStatus[] => {
    const contact: NotificationContact | null = selectedContact.value;
    if (contact === null) return [];
    if (contact.status === "active" || contact.status === "disabled") return ["active", "disabled"];
    return [];
  },
);
const contactStatusEditable: ComputedRef<boolean> = computed(
  (): boolean => contactStatusOptions.value.length > 0,
);
const canSaveSelectedContact: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedContactId.value === null ||
    selectedContact.value?.status === "active" ||
    selectedContact.value?.status === "pending" ||
    selectedContact.value?.status === "disabled",
);
const filteredContacts: ComputedRef<readonly NotificationContact[]> = computed(
  (): readonly NotificationContact[] => {
    const emailFilter: string = contactEmailFilter.value.trim().toLowerCase();
    const nameFilter: string = contactNameFilter.value.trim().toLowerCase();
    const statusFilter: string = contactStatusFilter.value;
    return contacts.value.filter((contact: NotificationContact): boolean => {
      return (
        contact.email.toLowerCase().includes(emailFilter) &&
        contact.displayName.toLowerCase().includes(nameFilter) &&
        (statusFilter === "" || contact.status === statusFilter)
      );
    });
  },
);
const filteredGroupContacts: ComputedRef<readonly NotificationContact[]> = computed(
  (): readonly NotificationContact[] => {
    const emailFilter: string = groupContactEmailFilter.value.trim().toLowerCase();
    const nameFilter: string = groupContactNameFilter.value.trim().toLowerCase();
    const statusFilter: string = groupContactStatusFilter.value;
    return contacts.value.filter((contact: NotificationContact): boolean => {
      return (
        contact.email.toLowerCase().includes(emailFilter) &&
        contact.displayName.toLowerCase().includes(nameFilter) &&
        (statusFilter === "" || contact.status === statusFilter)
      );
    });
  },
);

const tabs: readonly { readonly value: NotificationTab; readonly label: string }[] = [
  { value: "contacts", label: "Contacts" },
  { value: "groups", label: "Groups" },
  { value: "templates", label: "Templates" },
  { value: "policies", label: "Policies" },
];
const templatePlaceholders: readonly string[] = [
  "{{product_name}}",
  "{{recipient_name}}",
  "{{recipient_salutation}}",
  "{{tenant_name}}",
  "{{probe_name}}",
  "{{event_started_at}}",
  "{{event_resolved_at}}",
  "{{event_duration}}",
  "{{threshold_duration}}",
  "{{current_state}}",
  "{{dashboard_url}}",
];

function errorText(error: unknown): string {
  return error instanceof ApiRequestError ? error.message : "The notification request failed.";
}

function formatOptionalDate(value: string | null): string {
  return value === null ? "Not set" : formatDateTime(value, userSettingsStore.settings.dateTimeFormat);
}

function statusLabel(status: NotificationContactStatus): string {
  if (status === "unsubscribed") return "opted out";
  return status.replace("_", " ");
}

function contactStatusOptionLabel(status: EditableContactStatus): string {
  return status.replace("_", " ");
}

function statusTone(status: NotificationContactStatus): string {
  if (status === "active") return "active";
  if (status === "pending") return "pending";
  if (status === "unsubscribed" || status === "declined") return "opted-out";
  return "disabled";
}

function familyLabel(eventFamily: NotificationEventFamily): string {
  return eventFamily === "internet_interruption" ? "Internet interruption" : "Probe unavailable";
}

function tabFromQuery(value: unknown): NotificationTab {
  if (
    value === "contacts" ||
    value === "groups" ||
    value === "templates" ||
    value === "policies"
  ) {
    return value;
  }
  return "contacts";
}

async function selectTab(tab: NotificationTab): Promise<void> {
  activeTab.value = tab;
  await router.replace({
    name: "tenant-notifications",
    params: route.params,
    query: { ...route.query, tab },
  });
}

function minutesLabel(milliseconds: number): string {
  const minutes: number = Math.round(milliseconds / 60000);
  return `${minutes} min`;
}

function policyFor(eventFamily: NotificationEventFamily): NotificationEventPolicy | null {
  return policies.value.find(
    (policy: NotificationEventPolicy): boolean => policy.eventFamily === eventFamily,
  ) ?? null;
}

function policyDefaultSummary(eventFamily: NotificationEventFamily): string {
  const policy: NotificationEventPolicy | null = policyFor(eventFamily);
  if (policy === null) return "System default is not loaded.";
  const recovery: string = policy.systemSendRecovery ? "recovery on" : "opened only";
  return `${minutesLabel(policy.systemThresholdMs)}, ${recovery}`;
}

function policyEffectiveSummary(eventFamily: NotificationEventFamily): string {
  const policy: NotificationEventPolicy | null = policyFor(eventFamily);
  if (policy === null) return "Effective value is not loaded.";
  const enabled: string = policy.effectiveEnabled ? "enabled" : "disabled";
  const recovery: string = policy.effectiveSendRecovery ? "recovery on" : "opened only";
  const opened: string = policy.effectiveSendOpened ? "opened on" : "opened off";
  return `${enabled}, ${minutesLabel(policy.effectiveThresholdMs)}, ${opened}, ${recovery}`;
}

function thresholdInputValue(form: PolicyForm): number | string {
  if (form.thresholdOverrideEnabled) {
    return form.lastThresholdOverrideMinutes ?? form.thresholdMinutes;
  }
  const policy: NotificationEventPolicy | null = policyFor(form.eventFamily);
  if (policy === null) {
    return form.thresholdMinutes;
  }
  return Math.round(policy.systemThresholdMs / 60000);
}

function systemThresholdMinutes(eventFamily: NotificationEventFamily): number | null {
  const policy: NotificationEventPolicy | null = policyFor(eventFamily);
  return policy === null ? null : Math.round(policy.systemThresholdMs / 60000);
}

function updateThresholdOverride(form: PolicyForm, event: Event): void {
  const target: EventTarget | null = event.target;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  form.thresholdOverrideEnabled = target.checked;
  if (!form.thresholdOverrideEnabled) {
    const defaultMinutes: number | null = systemThresholdMinutes(form.eventFamily);
    form.thresholdMinutes = defaultMinutes ?? form.thresholdMinutes;
    return;
  }
  const overrideStart: number | string =
    form.lastThresholdOverrideMinutes ??
    systemThresholdMinutes(form.eventFamily) ??
    form.thresholdMinutes;
  form.thresholdMinutes = overrideStart;
  form.lastThresholdOverrideMinutes = overrideStart;
}

function updateThresholdMinutes(form: PolicyForm, event: Event): void {
  const target: EventTarget | null = event.target;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  form.thresholdMinutes = target.value;
  if (form.thresholdOverrideEnabled) {
    form.lastThresholdOverrideMinutes = target.value;
  }
}

function resetContactForm(): void {
  selectedContactId.value = null;
  contactEmail.value = "";
  contactDisplayName.value = "";
  contactSalutation.value = "";
  contactStatus.value = "pending";
}

function applyContact(contact: NotificationContact): void {
  selectedContactId.value = contact.id;
  contactEmail.value = contact.email;
  contactDisplayName.value = contact.displayName;
  contactSalutation.value = contact.salutation;
  contactStatus.value =
    contact.status === "active" || contact.status === "disabled" ? contact.status : "pending";
}

function resetGroupForm(): void {
  selectedGroupId.value = null;
  groupName.value = "";
  groupDescription.value = "";
  groupEnabled.value = true;
  groupContactIds.value = [];
}

function applyGroup(group: NotificationGroup): void {
  selectedGroupId.value = group.id;
  groupName.value = group.name;
  groupDescription.value = group.description;
  groupEnabled.value = group.enabled;
  groupContactIds.value = [...group.contactIds];
}

function resetTemplateForm(): void {
  selectedTemplateId.value = null;
  templateName.value = "";
  templateDescription.value = "";
  templateEnabled.value = true;
  templateSubject.value = "[{{tenant_name}}] {{probe_name}}: {{current_state}}";
  templateText.value = "{{recipient_salutation}}\n\n{{product_name}} detected {{current_state}} for {{probe_name}} in {{tenant_name}}.\n\nStarted: {{event_started_at}}\nThreshold: {{threshold_duration}}\nDetails: {{dashboard_url}}";
  templateHtml.value = "";
}

function applyTemplate(template: NotificationTemplate): void {
  selectedTemplateId.value = template.id;
  templateName.value = template.name;
  templateDescription.value = template.description;
  templateEnabled.value = template.enabled;
  templateSubject.value = template.subjectTemplate;
  templateText.value = template.textTemplate;
  templateHtml.value = template.htmlTemplate ?? "";
}

function policyFormFromPolicy(
  currentForm: PolicyForm,
  policy: NotificationEventPolicy,
): PolicyForm {
  const systemMinutes: number = Math.round(policy.systemThresholdMs / 60000);
  const overrideMinutes: number = Math.round(policy.thresholdOverrideMs / 60000);
  return {
    eventFamily: currentForm.eventFamily,
    enabled: policy.enabled,
    thresholdMinutes: policy.thresholdOverrideEnabled ? overrideMinutes : systemMinutes,
    lastThresholdOverrideMinutes: overrideMinutes,
    thresholdOverrideEnabled: policy.thresholdOverrideEnabled,
    sendOpened: policy.sendOpened,
    sendRecovery: policy.sendRecovery,
    openedTemplateId: policy.openedTemplateId ?? "",
    resolvedTemplateId: policy.resolvedTemplateId ?? "",
    groupIds: [...policy.groupIds],
  };
}

function applyPolicies(loadedPolicies: readonly NotificationEventPolicy[]): void {
  policyForms.value = policyForms.value.map((form: PolicyForm): PolicyForm => {
    const policy: NotificationEventPolicy | undefined = loadedPolicies.find(
      (item: NotificationEventPolicy): boolean => item.eventFamily === form.eventFamily,
    );
    if (policy === undefined) return form;
    return policyFormFromPolicy(form, policy);
  });
}

function applyPolicy(policy: NotificationEventPolicy): void {
  policyForms.value = policyForms.value.map((form: PolicyForm): PolicyForm =>
    form.eventFamily === policy.eventFamily ? policyFormFromPolicy(form, policy) : form,
  );
}

function localPolicyAfterSave(
  savedPolicy: NotificationEventPolicy,
  thresholdOverrideEnabled: boolean,
  thresholdOverrideMs: number,
): NotificationEventPolicy {
  const effectiveThresholdMs: number = savedPolicy.effectiveEnabled
    ? thresholdOverrideEnabled ? thresholdOverrideMs : savedPolicy.systemThresholdMs
    : savedPolicy.systemThresholdMs;
  return {
    ...savedPolicy,
    thresholdOverrideEnabled,
    thresholdOverrideMs,
    effectiveThresholdMs,
    effectiveSendRecovery: savedPolicy.effectiveEnabled
      ? savedPolicy.sendRecovery
      : savedPolicy.systemSendRecovery,
    effectiveSendOpened: savedPolicy.effectiveEnabled
      ? savedPolicy.sendOpened
      : savedPolicy.systemSendOpened,
    effectiveEnabled: savedPolicy.enabled,
  };
}

async function loadNotifications(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null) return;
  loading.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const result: [
      readonly NotificationContact[],
      readonly NotificationGroup[],
      readonly NotificationTemplate[],
      readonly NotificationEventPolicy[],
    ] = await Promise.all([
      listNotificationContacts(currentTenant.id),
      listNotificationGroups(currentTenant.id),
      listNotificationTemplates(currentTenant.id),
      listNotificationPolicies(currentTenant.id),
    ]);
    contacts.value = [...result[0]];
    groups.value = [...result[1]];
    templates.value = [...result[2]];
    policies.value = [...result[3]];
    applyPolicies(result[3]);
    if (selectedContact.value === null) resetContactForm();
    if (selectedGroup.value === null) resetGroupForm();
    if (selectedTemplate.value === null) resetTemplateForm();
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    loading.value = false;
  }
}

async function saveContact(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canManage.value) return;
  if (!canSaveSelectedContact.value) {
    errorMessage.value = "Opted-out or declined contacts require a new opt-in flow.";
    return;
  }
  saving.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const contactId: string | null = selectedContactId.value;
    const creating: boolean = contactId === null;
    const status: EditableContactStatus = contactStatusEditable.value
      ? contactStatus.value
      : "pending";
    const saved: NotificationContact = contactId === null
      ? await createNotificationContact(currentTenant.id, {
        email: contactEmail.value,
        displayName: contactDisplayName.value,
        salutation: contactSalutation.value,
      })
      : await updateNotificationContact(currentTenant.id, contactId, {
        displayName: contactDisplayName.value,
        salutation: contactSalutation.value,
        status,
      });
    await loadNotifications();
    applyContact(saved);
    message.value = creating ? "Contact created." : "Contact saved.";
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function removeContact(contact: NotificationContact): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canManage.value) return;
  const confirmed: boolean = await confirmDialog({
    title: "Delete notification contact?",
    message: `${contact.displayName} will be removed from notification groups and policies will no longer resolve this contact.`,
    confirmLabel: "Delete contact",
    tone: "danger",
  });
  if (!confirmed) return;
  saving.value = true;
  errorMessage.value = null;
  try {
    await deleteNotificationContact(currentTenant.id, contact.id);
    await loadNotifications();
    resetContactForm();
    message.value = "Contact deleted.";
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function saveGroup(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canManage.value) return;
  saving.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const groupId: string | null = selectedGroupId.value;
    const creating: boolean = groupId === null;
    const saved: NotificationGroup = groupId === null
      ? await createNotificationGroup(currentTenant.id, {
        name: groupName.value,
        description: groupDescription.value,
        enabled: groupEnabled.value,
        contactIds: groupContactIds.value,
      })
      : await updateNotificationGroup(currentTenant.id, groupId, {
        name: groupName.value,
        description: groupDescription.value,
        enabled: groupEnabled.value,
        contactIds: groupContactIds.value,
      });
    await loadNotifications();
    applyGroup(saved);
    message.value = creating ? "Group created." : "Group saved.";
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function removeGroup(group: NotificationGroup): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canManage.value) return;
  const confirmed: boolean = await confirmDialog({
    title: "Delete notification group?",
    message: `${group.name} will be removed from notification policies.`,
    confirmLabel: "Delete group",
    tone: "danger",
  });
  if (!confirmed) return;
  saving.value = true;
  errorMessage.value = null;
  try {
    await deleteNotificationGroup(currentTenant.id, group.id);
    await loadNotifications();
    resetGroupForm();
    message.value = "Group deleted.";
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function saveTemplate(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canManage.value) return;
  saving.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const templateId: string | null = selectedTemplateId.value;
    const creating: boolean = templateId === null;
    const saved: NotificationTemplate = templateId === null
      ? await createNotificationTemplate(currentTenant.id, {
        name: templateName.value,
        description: templateDescription.value,
        enabled: templateEnabled.value,
        subjectTemplate: templateSubject.value,
        textTemplate: templateText.value,
        htmlTemplate: templateHtml.value.trim() === "" ? null : templateHtml.value,
      })
      : await updateNotificationTemplate(currentTenant.id, templateId, {
        name: templateName.value,
        description: templateDescription.value,
        enabled: templateEnabled.value,
        subjectTemplate: templateSubject.value,
        textTemplate: templateText.value,
        htmlTemplate: templateHtml.value.trim() === "" ? null : templateHtml.value,
      });
    await loadNotifications();
    applyTemplate(saved);
    message.value = creating ? "Template created." : "Template saved.";
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function removeTemplate(template: NotificationTemplate): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canManage.value) return;
  const confirmed: boolean = await confirmDialog({
    title: "Delete notification template?",
    message: `${template.name} will be removed from policies that use it.`,
    confirmLabel: "Delete template",
    tone: "danger",
  });
  if (!confirmed) return;
  saving.value = true;
  errorMessage.value = null;
  try {
    await deleteNotificationTemplate(currentTenant.id, template.id);
    await loadNotifications();
    resetTemplateForm();
    message.value = "Template deleted.";
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

async function savePolicy(form: PolicyForm): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canManage.value) return;
  const thresholdOverrideEnabled: boolean = form.thresholdOverrideEnabled === true;
  const thresholdValue: number | string = thresholdOverrideEnabled
    ? form.lastThresholdOverrideMinutes ?? form.thresholdMinutes
    : form.lastThresholdOverrideMinutes ?? form.thresholdMinutes;
  const minutes: number = Number(thresholdValue);
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
    errorMessage.value = "Threshold override must be between 0 and 1440 minutes.";
    return;
  }
  saving.value = true;
  errorMessage.value = null;
  message.value = null;
  try {
    const thresholdOverrideMs: number = minutes * 60000;
    const saved: NotificationEventPolicy = await updateNotificationPolicy(currentTenant.id, form.eventFamily, {
      enabled: form.enabled,
      thresholdOverrideEnabled,
      thresholdOverrideMs,
      sendOpened: form.sendOpened,
      sendRecovery: form.sendRecovery,
      openedTemplateId: form.openedTemplateId === "" ? null : form.openedTemplateId,
      resolvedTemplateId: form.resolvedTemplateId === "" ? null : form.resolvedTemplateId,
      groupIds: form.groupIds,
    });
    const localPolicy: NotificationEventPolicy = localPolicyAfterSave(
      saved,
      thresholdOverrideEnabled,
      thresholdOverrideMs,
    );
    policies.value = [
      ...policies.value.filter((policy: NotificationEventPolicy): boolean => policy.eventFamily !== localPolicy.eventFamily),
      localPolicy,
    ].sort((left: NotificationEventPolicy, right: NotificationEventPolicy): number =>
      left.eventFamily.localeCompare(right.eventFamily),
    );
    applyPolicy(localPolicy);
    message.value = `${familyLabel(form.eventFamily)} policy saved.`;
  } catch (error: unknown) {
    errorMessage.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

onMounted(loadNotifications);
watch(
  (): unknown => route.query.tab,
  async (value: unknown): Promise<void> => {
    const normalizedTab: NotificationTab = tabFromQuery(
      Array.isArray(value) ? value[0] : value,
    );
    activeTab.value = normalizedTab;
    if (value !== normalizedTab) {
      await router.replace({
        name: "tenant-notifications",
        params: route.params,
        query: { ...route.query, tab: normalizedTab },
      });
    }
  },
  { immediate: true },
);
watch(
  (): string | null => tenant.value?.id ?? null,
  async (): Promise<void> => {
    resetContactForm();
    resetGroupForm();
    resetTemplateForm();
    await loadNotifications();
  },
);
</script>

<template>
  <AppShell v-if="tenant">
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ tenant.displayName }}</p>
        <h1>Tenant notifications</h1>
        <p>Tenant-owned recipients, groups, templates, and event policies.</p>
      </div>
    </header>

    <div class="page-tabs notification-tabs" role="tablist" aria-label="Notification settings">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="{ 'router-link-active': activeTab === tab.value }"
        class="page-tab"
        @click="selectTab(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>

    <p v-if="errorMessage" class="form-error settings-load-error" role="alert">{{ errorMessage }}</p>
    <p v-if="message" class="settings-message" role="status">{{ message }}</p>
    <p v-if="loading" class="empty-state">Loading notification settings…</p>

    <section v-if="activeTab === 'contacts'" class="content-panel notification-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Recipients</p>
          <h2>Contacts</h2>
        </div>
        <span class="coming-soon">{{ activeContactCount }} / {{ tenant.tier.maxNotificationContactsPerTenant }} pending or active</span>
      </div>
      <form class="settings-form notification-form notification-contact-details" @submit.prevent="saveContact">
        <div class="settings-card__heading notification-contact-details__heading">
          <div>
            <p class="eyebrow">{{ selectedContactId === null ? "New contact" : "Edit contact" }}</p>
            <h2>Contact details</h2>
          </div>
          <button class="button button--ghost button--compact" type="button" @click="resetContactForm">New</button>
        </div>
        <label>
          <span>Email</span>
          <input v-model="contactEmail" type="email" required maxlength="320" :disabled="selectedContactId !== null || !canManage" />
        </label>
        <label>
          <span>Name</span>
          <input v-model="contactDisplayName" required maxlength="200" :disabled="!canManage || !canSaveSelectedContact" />
        </label>
        <label>
          <span>Salutation</span>
          <input v-model="contactSalutation" maxlength="500" placeholder="Hello Alex," :disabled="!canManage || !canSaveSelectedContact" />
        </label>
        <label v-if="selectedContact && contactStatusEditable">
          <span>Status</span>
          <select v-model="contactStatus" :disabled="!canManage">
            <option
              v-for="status in contactStatusOptions"
              :key="status"
              :value="status"
            >
              {{ contactStatusOptionLabel(status) }}
            </option>
          </select>
        </label>
        <label v-else-if="selectedContact">
          <span>Status</span>
          <input :value="statusLabel(selectedContact.status)" readonly disabled />
        </label>
        <div v-if="selectedContact" class="notification-facts notification-contact-details__facts">
          <span>Verified: {{ formatOptionalDate(selectedContact.verifiedAt) }}</span>
          <span>Opt-out: {{ formatOptionalDate(selectedContact.unsubscribedAt) }}</span>
        </div>
        <div class="settings-actions notification-contact-details__actions">
          <button v-if="selectedContact" class="button button--danger" type="button" :disabled="saving || !canManage" @click="removeContact(selectedContact)">Delete</button>
          <button class="button button--primary" type="submit" :disabled="saving || !canManage || !canSaveSelectedContact">{{ saving ? "Saving…" : "Save" }}</button>
        </div>
      </form>

      <div class="notification-contact-filters notification-contact-filters--compact">
        <label>
          <span>Email contains</span>
          <input v-model="contactEmailFilter" placeholder="ls.name@shadowsoft.de" />
        </label>
        <label>
          <span>Name contains</span>
          <input v-model="contactNameFilter" placeholder="Ada" />
        </label>
        <label>
          <span>Status</span>
          <select v-model="contactStatusFilter">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="unsubscribed">Opted out</option>
            <option value="disabled">Disabled</option>
          </select>
        </label>
      </div>

      <div class="notification-contact-table-wrap">
        <table class="notification-contact-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Email</th>
              <th>Name</th>
              <th>Salutation</th>
              <th>Verified</th>
              <th>Opt-out</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="contact in filteredContacts"
              :key="contact.id"
              :class="{ 'notification-contact-table__row--selected': selectedContactId === contact.id }"
              @click="applyContact(contact)"
            >
              <td>
                <span class="notification-contact-status">
                  <span
                    class="notification-contact-status__dot"
                    :class="`notification-contact-status__dot--${statusTone(contact.status)}`"
                    aria-hidden="true"
                  ></span>
                  {{ statusLabel(contact.status) }}
                </span>
              </td>
              <td>{{ contact.email }}</td>
              <td>{{ contact.displayName }}</td>
              <td>{{ contact.salutation }}</td>
              <td>{{ formatOptionalDate(contact.verifiedAt) }}</td>
              <td>{{ formatOptionalDate(contact.unsubscribedAt) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="contacts.length === 0" class="prepared-empty-state__safety">No notification contacts exist yet.</p>
        <p v-else-if="filteredContacts.length === 0" class="prepared-empty-state__safety">No contacts match the current filters.</p>
      </div>
    </section>

    <section v-if="activeTab === 'groups'" class="content-panel notification-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Recipient sets</p>
          <h2>Groups</h2>
        </div>
        <span class="coming-soon">{{ groups.length }} configured</span>
      </div>
      <div class="notification-layout">
        <div class="notification-list">
          <button
            v-for="group in groups"
            :key="group.id"
            type="button"
            class="notification-list__item"
            :class="{ 'notification-list__item--selected': selectedGroupId === group.id }"
            @click="applyGroup(group)"
          >
            <strong>{{ group.name }}</strong>
            <span>{{ group.contactIds.length }} contact{{ group.contactIds.length === 1 ? "" : "s" }}</span>
            <small>{{ group.enabled ? "enabled" : "disabled" }}</small>
          </button>
          <p v-if="groups.length === 0" class="prepared-empty-state__safety">No notification groups exist yet.</p>
        </div>

        <form class="settings-form notification-form" @submit.prevent="saveGroup">
          <div class="settings-card__heading">
            <div><p class="eyebrow">{{ selectedGroupId === null ? "New group" : "Edit group" }}</p><h2>Group details</h2></div>
            <button class="button button--ghost button--compact" type="button" @click="resetGroupForm">New</button>
          </div>
          <label>
            <span>Name</span>
            <input v-model="groupName" required maxlength="200" :disabled="!canManage" />
          </label>
          <label>
            <span>Enabled</span>
            <select v-model="groupEnabled" :disabled="!canManage">
              <option :value="true">Enabled</option>
              <option :value="false">Disabled</option>
            </select>
          </label>
          <label class="settings-field--wide">
            <span>Description</span>
            <input v-model="groupDescription" maxlength="2000" :disabled="!canManage" />
          </label>
          <div class="settings-field--wide notification-group-membership">
            <div class="notification-group-membership__heading">
              <div>
                <p class="eyebrow">Membership</p>
                <h3>Contacts</h3>
              </div>
              <span class="coming-soon">{{ groupContactIds.length }} selected</span>
            </div>
            <div class="notification-contact-filters notification-contact-filters--compact">
              <label>
                <span>Email contains</span>
                <input v-model="groupContactEmailFilter" placeholder="ls.name@shadowsoft.de" />
              </label>
              <label>
                <span>Name contains</span>
                <input v-model="groupContactNameFilter" placeholder="Ada" />
              </label>
              <label>
                <span>Status</span>
                <select v-model="groupContactStatusFilter">
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="unsubscribed">Opted out</option>
                  <option value="disabled">Disabled</option>
                </select>
              </label>
            </div>
            <div class="notification-contact-table-wrap">
              <table class="notification-contact-table notification-contact-table--membership">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Status</th>
                    <th>Email</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="contact in filteredGroupContacts"
                    :key="contact.id"
                    :class="{ 'notification-contact-table__row--selected': groupContactIds.includes(contact.id) }"
                  >
                    <td>
                      <input v-model="groupContactIds" type="checkbox" :value="contact.id" :disabled="!canManage" />
                    </td>
                    <td>
                      <span class="notification-contact-status">
                        <span
                          class="notification-contact-status__dot"
                          :class="`notification-contact-status__dot--${statusTone(contact.status)}`"
                          aria-hidden="true"
                        ></span>
                        {{ statusLabel(contact.status) }}
                      </span>
                    </td>
                    <td>{{ contact.email }}</td>
                    <td>{{ contact.displayName }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-if="contacts.length === 0" class="prepared-empty-state__safety">Create contacts before assigning group members.</p>
              <p v-else-if="filteredGroupContacts.length === 0" class="prepared-empty-state__safety">No contacts match the current filters.</p>
            </div>
          </div>
          <div class="settings-actions settings-field--wide">
            <button v-if="selectedGroup" class="button button--danger" type="button" :disabled="saving || !canManage" @click="removeGroup(selectedGroup)">Delete</button>
            <button class="button button--primary" type="submit" :disabled="saving || !canManage">{{ saving ? "Saving…" : "Save group" }}</button>
          </div>
        </form>
      </div>
    </section>

    <section v-if="activeTab === 'templates'" class="content-panel notification-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Message library</p>
          <h2>Templates</h2>
        </div>
        <span class="coming-soon">{{ templates.length }} tenant templates</span>
      </div>
      <div class="notification-layout notification-layout--templates">
        <div class="notification-list">
          <button
            v-for="template in templates"
            :key="template.id"
            type="button"
            class="notification-list__item"
            :class="{ 'notification-list__item--selected': selectedTemplateId === template.id }"
            @click="applyTemplate(template)"
          >
            <strong>{{ template.name }}</strong>
            <span>Revision {{ template.revision }}</span>
            <small>{{ template.enabled ? "enabled" : "disabled" }}</small>
          </button>
          <p v-if="templates.length === 0" class="prepared-empty-state__safety">No tenant templates exist yet. Policies can still use system defaults.</p>
        </div>

        <form class="settings-form notification-form notification-template-form" @submit.prevent="saveTemplate">
          <div class="settings-card__heading">
            <div><p class="eyebrow">{{ selectedTemplateId === null ? "New template" : "Edit template" }}</p><h2>Template details</h2></div>
            <button class="button button--ghost button--compact" type="button" @click="resetTemplateForm">New</button>
          </div>
          <label>
            <span>Name</span>
            <input v-model="templateName" required maxlength="200" :disabled="!canManage" />
          </label>
          <label>
            <span>Enabled</span>
            <select v-model="templateEnabled" :disabled="!canManage">
              <option :value="true">Enabled</option>
              <option :value="false">Disabled</option>
            </select>
          </label>
          <label class="settings-field--wide">
            <span>Description</span>
            <input v-model="templateDescription" maxlength="4000" :disabled="!canManage" />
          </label>
          <label class="settings-field--wide">
            <span>Subject</span>
            <input v-model="templateSubject" required maxlength="320" :disabled="!canManage" />
          </label>
          <label class="settings-field--wide">
            <span>Text body</span>
            <textarea v-model="templateText" required rows="12" maxlength="100000" :disabled="!canManage"></textarea>
          </label>
          <label class="settings-field--wide">
            <span>HTML body · optional</span>
            <textarea v-model="templateHtml" rows="8" maxlength="200000" :disabled="!canManage"></textarea>
          </label>
          <div class="template-placeholders settings-field--wide">
            <p>Allowed placeholders</p>
            <div>
              <code v-for="placeholder in templatePlaceholders" :key="placeholder">{{ placeholder }}</code>
            </div>
          </div>
          <div class="settings-actions settings-field--wide">
            <button v-if="selectedTemplate" class="button button--danger" type="button" :disabled="saving || !canManage" @click="removeTemplate(selectedTemplate)">Delete</button>
            <button class="button button--primary" type="submit" :disabled="saving || !canManage">{{ saving ? "Saving…" : "Save template" }}</button>
          </div>
        </form>
      </div>
    </section>

    <section v-if="activeTab === 'policies'" class="content-panel notification-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Alert delivery</p>
          <h2>Overrides</h2>
        </div>
        <span class="coming-soon">System defaults are used for fields set to default</span>
      </div>

      <div class="notification-policy-list">
        <form
          v-for="form in policyForms"
          :key="form.eventFamily"
          class="settings-form notification-policy-card"
          :class="{ 'notification-policy-card--disabled': !form.enabled }"
          @submit.prevent="savePolicy(form)"
        >
          <div class="settings-card__heading">
            <div>
              <p class="eyebrow">Event family</p>
              <h2>{{ familyLabel(form.eventFamily) }}</h2>
            </div>
            <label class="notification-toggle">
              <input v-model="form.enabled" type="checkbox" :disabled="!canManage" />
              <span>Enabled</span>
            </label>
          </div>
          <div class="notification-facts settings-field--wide">
            <span>Default: {{ policyDefaultSummary(form.eventFamily) }}</span>
            <span>Effective: {{ policyEffectiveSummary(form.eventFamily) }}</span>
          </div>
          <div class="notification-threshold-control">
            <label>
              <span>Threshold minutes</span>
              <input
                :value="thresholdInputValue(form)"
                type="number"
                min="0"
                max="1440"
                inputmode="numeric"
                :readonly="!form.thresholdOverrideEnabled"
                :disabled="!canManage || !form.enabled"
                :class="{ 'notification-default-value': !form.thresholdOverrideEnabled }"
                @input="updateThresholdMinutes(form, $event)"
              />
            </label>
            <label class="notification-toggle notification-threshold-control__override">
              <input
                :checked="form.thresholdOverrideEnabled"
                type="checkbox"
                :disabled="!canManage || !form.enabled"
                @change="updateThresholdOverride(form, $event)"
              />
              <span>Override</span>
            </label>
          </div>
          <label
            class="notification-template-field"
            :class="{ 'notification-template-field--disabled': !form.sendOpened }"
          >
            <span class="notification-template-toggle">
              <input v-model="form.sendOpened" type="checkbox" :disabled="!canManage || !form.enabled" />
              <span>Send opened event</span>
            </span>
            <select v-model="form.openedTemplateId" :disabled="!canManage || !form.enabled || !form.sendOpened">
              <option value="">System default</option>
              <option v-for="template in templates" :key="template.id" :value="template.id" :disabled="!template.enabled">{{ template.name }}</option>
            </select>
          </label>
          <label
            class="notification-template-field"
            :class="{ 'notification-template-field--disabled': !form.sendRecovery }"
          >
            <span class="notification-template-toggle">
              <input v-model="form.sendRecovery" type="checkbox" :disabled="!canManage || !form.enabled" />
              <span>Send resolved event</span>
            </span>
            <select v-model="form.resolvedTemplateId" :disabled="!canManage || !form.enabled || !form.sendRecovery">
              <option value="">System default</option>
              <option v-for="template in templates" :key="template.id" :value="template.id" :disabled="!template.enabled">{{ template.name }}</option>
            </select>
          </label>
          <fieldset class="settings-field--wide notification-checkboxes">
            <legend>Target groups</legend>
            <label v-for="group in groups" :key="group.id">
              <input v-model="form.groupIds" type="checkbox" :value="group.id" :disabled="!canManage || !form.enabled || !group.enabled" />
              <span>{{ group.name }} · {{ group.contactIds.length }} contact{{ group.contactIds.length === 1 ? "" : "s" }}</span>
            </label>
            <p v-if="groups.length === 0" class="prepared-empty-state__safety">No groups assigned means alerts can exist without outbound deliveries.</p>
          </fieldset>
          <div class="settings-actions settings-field--wide">
            <button class="button button--primary" type="submit" :disabled="saving || !canManage">{{ saving ? "Saving…" : "Save policy" }}</button>
          </div>
        </form>
      </div>
    </section>
  </AppShell>
</template>
